import React, { useMemo, useState } from "react";
import { Box, MenuItem, Typography } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { AxisConfig } from "@mui/x-charts/models";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import { useWorkspaceColors } from "../../../Contexts/WorkspaceColorContext";
import { Features } from "../../../Enums/companyStatus";
import { hasPermission } from "../../../Helpers/authHelpers";
import {
  VoicesUsageActionKey,
  VoicesUsageFilters,
  VoicesUsageGroup,
  VoicesUsageTotals,
} from "../../../Models/voicesUsage";
import { useVoicesUsage } from "../../../Query/useVoicesUsage";
import { EmptyState } from "../../HomePage/DashboardCard";
import Loading from "../../Common/Loading";
import { dayKey } from "../Calendar/voicesCalendarDates";
import VoicesCard from "../Common/VoicesCard";
import VoicesInput from "../Common/VoicesInput";
import VoicesUsagePeriod from "./VoicesUsagePeriod";

const ACTIONS: VoicesUsageActionKey[] = ["radar_scoring", "web_search", "post_draft"];

const ACTION_COLORS: Record<VoicesUsageActionKey, string> = {
  radar_scoring: "#2a78d6",
  web_search: "#eb6834",
  post_draft: "#1baf7a",
};

const daysAgo = (days: number) => {
  const day = new Date();
  day.setDate(day.getDate() - days);
  return dayKey(day);
};

const usd = (value: number) =>
  value.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: value > 0 && value < 1 ? 4 : 2,
  });

const change = (current: number, previous: number) =>
  previous > 0 ? Math.round(((current - previous) / previous) * 100) : null;

const Tile: React.FC<{ label: string; value: string; note?: string }> = ({
  label,
  value,
  note,
}) => (
  <Box
    sx={{
      p: 1.5,
      borderRadius: "12px",
      backgroundColor: color.gray50,
      border: `1px solid ${color.gray100}`,
      minWidth: 0,
    }}
  >
    <Typography sx={{ fontSize: 12, color: color.gray500 }}>{label}</Typography>
    <Typography sx={{ fontSize: 22, fontWeight: 700, color: color.gray900 }}>{value}</Typography>
    {note && <Typography sx={{ fontSize: 12, color: color.gray600 }}>{note}</Typography>}
  </Box>
);

const UsageTable: React.FC<{
  title: string;
  rows: { key: string; label: string; cost: number; calls: number; dot?: string }[];
  emptyText: string;
}> = ({ title, rows, emptyText }) => {
  const { t } = useTranslation("voicesUsage");
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: color.gray900, mb: 1 }}>
        {title}
      </Typography>
      {!rows.length ? (
        <Typography sx={{ fontSize: 13, color: color.gray500 }}>{emptyText}</Typography>
      ) : (
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <Box component="thead">
            <Box component="tr" sx={{ "& th": { color: color.gray500, fontWeight: 600, pb: 0.75 } }}>
              <Box component="th" sx={{ textAlign: "left" }} />
              <Box component="th" sx={{ textAlign: "right" }}>
                {t("calls")}
              </Box>
              <Box component="th" sx={{ textAlign: "right" }}>
                {t("cost")}
              </Box>
            </Box>
          </Box>
          <Box component="tbody">
            {rows.map((row) => (
              <Box
                component="tr"
                key={row.key}
                sx={{
                  "& td": { py: 0.75, borderTop: `1px solid ${color.gray100}`, color: color.gray900 },
                }}
              >
                <Box component="td">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {row.dot && (
                      <Box
                        aria-hidden
                        sx={{ width: 10, height: 10, borderRadius: "3px", backgroundColor: row.dot }}
                      />
                    )}
                    {row.label}
                  </Box>
                </Box>
                <Box component="td" sx={{ textAlign: "right", color: `${color.gray600} !important` }}>
                  {row.calls.toLocaleString()}
                </Box>
                <Box component="td" sx={{ textAlign: "right", fontWeight: 600 }}>
                  {usd(row.cost)}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const VoicesUsage: React.FC = () => {
  const { t, i18n } = useTranslation("voicesUsage");
  const { colors: workspace } = useWorkspaceColors();
  const isMarketing = hasPermission(Features.VOICES_MARKETING);
  const [preset, setPreset] = useState<number | null>(30);
  const [customFrom, setCustomFrom] = useState(daysAgo(29));
  const [customTo, setCustomTo] = useState(dayKey(new Date()));
  const [campaignId, setCampaignId] = useState<number | "">("");
  const [userId, setUserId] = useState<number | "">("");

  const filters = useMemo<VoicesUsageFilters>(() => {
    const range =
      preset === null
        ? { from: customFrom, to: customTo }
        : { from: daysAgo(preset - 1), to: dayKey(new Date()) };
    return {
      ...range,
      ...(campaignId ? { campaign_id: campaignId } : {}),
      ...(userId ? { user_id: userId } : {}),
    };
  }, [preset, customFrom, customTo, campaignId, userId]);

  const { data: usage, isLoading } = useVoicesUsage(filters);

  if (isLoading || !usage) {
    return (
      <Loading backgroundColor={color.gray50} color1={workspace.accent} color2={color.gray900} />
    );
  }

  const costChange = change(usage.totals.cost, usage.previous.cost);
  const tokens = (totals: VoicesUsageTotals) => totals.input_tokens + totals.output_tokens;
  const groupRows = (groups: VoicesUsageGroup[], fallback: string) =>
    groups.map((group) => ({
      key: String(group.id),
      label: group.name || fallback,
      cost: group.cost,
      calls: group.calls,
    }));
  const dayLabel = (value: string) =>
    new Date(`${value}T00:00:00`).toLocaleDateString(i18n.language, {
      day: "numeric",
      month: "short",
    });
  const dayAxis: AxisConfig<"band", string> = {
    id: "days",
    scaleType: "band",
    data: usage.daily.map((day) => day.date),
    valueFormatter: (value: string) => dayLabel(value),
    categoryGapRatio: usage.daily.length > 30 ? 0.2 : 0.35,
  };

  const filterBar = (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", mb: 3 }}>
      <VoicesUsagePeriod
        preset={preset}
        from={filters.from}
        to={filters.to}
        onPreset={setPreset}
        onCustom={(from, to) => {
          setCustomFrom(from);
          setCustomTo(to);
          setPreset(null);
        }}
      />
      <VoicesInput
        select
        size="small"
        value={campaignId}
        onChange={(e) => setCampaignId(e.target.value === "" ? "" : Number(e.target.value))}
        sx={{ width: 210 }}
      >
        <MenuItem value="">{t("allCampaigns")}</MenuItem>
        {usage.options.campaigns.map((campaign) => (
          <MenuItem key={campaign.id} value={campaign.id}>
            {campaign.name}
          </MenuItem>
        ))}
      </VoicesInput>
      {isMarketing && (
        <VoicesInput
          select
          size="small"
          value={userId}
          onChange={(e) => setUserId(e.target.value === "" ? "" : Number(e.target.value))}
          sx={{ width: 210 }}
        >
          <MenuItem value="">{t("everyone")}</MenuItem>
          {usage.options.people.map((person) => (
            <MenuItem key={person.id} value={person.id}>
              {person.name}
            </MenuItem>
          ))}
        </VoicesInput>
      )}
    </Box>
  );

  return (
    <VoicesCard title={t("title")} subtitle={t("subtitle")}>
      {filterBar}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
          gap: 1.5,
          mb: 3,
        }}
      >
        <Tile
          label={t("totalCost")}
          value={usd(usage.totals.cost)}
          note={
            costChange === null
              ? t("noPrevious")
              : t("comparedToPrevious", {
                  change: `${costChange > 0 ? "+" : ""}${costChange} %`,
                  previous: usd(usage.previous.cost),
                })
          }
        />
        <Tile label={t("aiCalls")} value={usage.totals.calls.toLocaleString()} />
        <Tile label={t("webSearches")} value={usage.totals.searches.toLocaleString()} />
        <Tile
          label={t("tokens")}
          value={tokens(usage.totals).toLocaleString()}
          note={t("tokensSplit", {
            input: usage.totals.input_tokens.toLocaleString(),
            output: usage.totals.output_tokens.toLocaleString(),
          })}
        />
      </Box>

      {!usage.totals.calls ? (
        <EmptyState>{t("empty")}</EmptyState>
      ) : (
        <>
          {usage.daily.length > 1 && (
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: color.gray900 }}>
                {t("dailyCost")}
              </Typography>
              <BarChart
                height={260}
                xAxis={[dayAxis]}
                yAxis={[{ valueFormatter: (value: number) => usd(value) }]}
                series={ACTIONS.map((action) => ({
                  data: usage.daily.map((day) => day[action]),
                  label: t(`action_${action}`),
                  color: ACTION_COLORS[action],
                  stack: "cost",
                  valueFormatter: (value: number | null) => usd(value ?? 0),
                }))}
                margin={{ left: 70 }}
                slotProps={{ legend: { labelStyle: { fill: color.gray700, fontSize: 12 } } }}
                sx={{
                  "& .MuiChartsAxis-tickLabel": { fill: `${color.gray600} !important` },
                  "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": {
                    stroke: `${color.gray300} !important`,
                  },
                }}
              />
            </Box>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" },
              gap: 3,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <UsageTable
                title={t("byAction")}
                emptyText={t("empty")}
                rows={usage.by_action
                  .filter((item) => item.calls)
                  .map((item) => ({
                    key: item.action,
                    label: `${t(`action_${item.action}`)} · ${t("perCall", { cost: usd(item.average) })}`,
                    cost: item.cost,
                    calls: item.calls,
                    dot: ACTION_COLORS[item.action],
                  }))}
              />
            </Box>
            {!campaignId && (
              <UsageTable
                title={t("byCampaign")}
                emptyText={t("empty")}
                rows={groupRows(usage.by_campaign, t("noCampaign"))}
              />
            )}
            <UsageTable
              title={t("byModel")}
              emptyText={t("empty")}
              rows={groupRows(usage.by_model, "–")}
            />
            {isMarketing && !userId && (
              <UsageTable
                title={t("byPerson")}
                emptyText={t("noPersonUsage")}
                rows={groupRows(usage.by_person, "–")}
              />
            )}
          </Box>
          <Typography sx={{ fontSize: 12, color: color.gray500, mt: 3 }}>{t("note")}</Typography>
        </>
      )}
    </VoicesCard>
  );
};

export default VoicesUsage;
